import React from 'react';
import DOMPurify from 'dompurify';
import styles from './ResumeDraft.module.css';
import HistoryViewer from './HistoryViewer';
import { Clock } from 'lucide-react';
import Tooltip from './Tooltip';

interface ResumeDraftProps {
  document: string;
  isOpen: boolean;
  onToggle: () => void;
  onResize?: (width: number) => void;
  width?: number;
  isEmbedded?: boolean;
  isOpenWide?: boolean;
  onToggleWide?: () => void;
  versionId?: string;
  onHistoryRestore?: () => void;
}

// Helper function to check if the document has actual resume content
const hasResumeContent = (document: string): boolean => {
  if (!document) return false;
  
  // Create a temporary div to parse the HTML
  const tempDiv = window.document.createElement('div');
  tempDiv.innerHTML = document;
  
  // Check if there's any content in the resume container
  const resumeContainer = tempDiv.querySelector('.resume-container');
  if (!resumeContainer) return false;
  
  // Get text content and check if it has any non-whitespace characters
  const textContent = resumeContainer.textContent || '';
  return textContent.trim().length > 0;
};

const ResumeDraft: React.FC<ResumeDraftProps> = ({
  document,
  isOpen,
  onToggle,
  onResize,
  width = 400,
  isEmbedded = false,
  isOpenWide = false,
  onToggleWide,
  versionId,
  onHistoryRestore
}) => {
  const [isResizing, setIsResizing] = React.useState(false);
  const [startX, setStartX] = React.useState(0);
  const [startWidth, setStartWidth] = React.useState(width);
  const [animationState, setAnimationState] = React.useState<'entering' | 'entered' | 'exiting' | 'exited'>('exited');
  const [scale, setScale] = React.useState(1);
  const [previousWidth, setPreviousWidth] = React.useState(width);
  const [pageCount, setPageCount] = React.useState(1);
  const [contentHeight, setContentHeight] = React.useState(0);
  const [processedDocument, setProcessedDocument] = React.useState<string>('');
  const [showHistory, setShowHistory] = React.useState(false);
  const paperWrapperRef = React.useRef<HTMLDivElement>(null);
  const hiddenIframeRef = React.useRef<HTMLIFrameElement>(null);

  const handleMouseDown = (e: React.MouseEvent) => {
    // Prevent resizing when in "open wide" mode
    if (isOpenWide) return;
    
    setIsResizing(true);
    setStartX(e.clientX);
    setStartWidth(width);
    e.preventDefault();
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!isResizing) return;
      
      const deltaX = startX - e.clientX;
      const newWidth = Math.max(300, Math.min(800, startWidth + deltaX));
      
      if (onResize) {
        onResize(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      window.document.addEventListener('mousemove', handleMouseMove);
      window.document.addEventListener('mouseup', handleMouseUp);
      window.document.body.style.cursor = 'col-resize';
      window.document.body.style.userSelect = 'none';
    }

    return () => {
      window.document.removeEventListener('mousemove', handleMouseMove);
      window.document.removeEventListener('mouseup', handleMouseUp);
      window.document.body.style.cursor = '';
      window.document.body.style.userSelect = '';
    };
  }, [isResizing, startX, startWidth, onResize]);

  // Store previous width when toggling to wide mode
  React.useEffect(() => {
    if (isOpenWide) {
      setPreviousWidth(width);
    }
  }, [isOpenWide, width]);

  // Set CSS custom property for panel width
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      window.document.documentElement.style.setProperty('--panel-width', `${width}px`);
    }
  }, [width]);

  // Calculate scale for the paper preview
  React.useEffect(() => {
    const calculateScale = () => {
      if (paperWrapperRef.current && document && hasResumeContent(document)) {
        // Use actual container width - either wide mode (80vw) or normal mode (width prop)
        const actualWidth = isOpenWide ? (window.innerWidth * 0.8) : width;
        const containerWidth = actualWidth - 80; // Account for padding (30px * 2) + some buffer
        const paperWidth = 816; // Fixed paper width
        const newScale = Math.min(containerWidth / paperWidth, 1);
        setScale(newScale);
      }
    };

    calculateScale();

    // Add window resize listener for wide mode
    if (isOpenWide) {
      window.addEventListener('resize', calculateScale);
      return () => window.removeEventListener('resize', calculateScale);
    }
  }, [width, document, isOpenWide]);

  // Calculate number of pages needed
  React.useEffect(() => {
    if (!document || !hiddenIframeRef.current || !hasResumeContent(document)) return;

    const checkContentHeight = () => {
      try {
        const iframeDoc = hiddenIframeRef.current?.contentDocument || hiddenIframeRef.current?.contentWindow?.document;
        if (!iframeDoc) {
          return;
        }

        // Wait for content to load
        if (iframeDoc.readyState !== 'complete') {
          hiddenIframeRef.current?.addEventListener('load', checkContentHeight);
          return;
        }

        const body = iframeDoc.body;
        if (!body) {
          return;
        }

        // First, remove any existing force-page-break elements
        const existingBreaks = iframeDoc.querySelectorAll('.force-page-break');
        existingBreaks.forEach(el => el.remove());

        const resumeContainer = iframeDoc.querySelector('.resume-container');
        if (!resumeContainer) {
          return;
        }

        // Find all headers that should stay with their content
        const headers = iframeDoc.querySelectorAll('.job-header, .section-title, .education-header');
        const pageHeight = 1056; // A4 height in pixels
        const dangerZone = 150; // Pixels from bottom of page where headers shouldn't appear

        // Insert page breaks before headers that are too close to page bottom
        headers.forEach((header: Element) => {
          const headerElement = header as HTMLElement;
          
          // Check if we should look at the parent container instead
          let elementToCheck = headerElement;
          const parentItem = headerElement.closest('.employment-item, .education-item, .resume-section');
          if (parentItem) {
            elementToCheck = parentItem as HTMLElement;
          }
          
          const elementTop = elementToCheck.offsetTop;
          const pageNumber = Math.floor(elementTop / pageHeight);
          const positionInPage = elementTop - (pageNumber * pageHeight);
          
          // If element is in the danger zone at bottom of page
          if (positionInPage > (pageHeight - dangerZone) && positionInPage < pageHeight) {
            
            // Create and insert a page break element before the element
            const pageBreak = iframeDoc.createElement('div');
            pageBreak.className = 'force-page-break';
            elementToCheck.parentNode?.insertBefore(pageBreak, elementToCheck);
          }
        });

        // Recalculate height after adding page breaks
        const finalHeight = resumeContainer.scrollHeight;
        const pagesNeeded = Math.ceil(finalHeight / pageHeight);
        
        // Get the modified HTML with page breaks
        const modifiedHtml = iframeDoc.documentElement.outerHTML;
        setProcessedDocument(modifiedHtml);
        
        setContentHeight(finalHeight);
        setPageCount(Math.max(1, pagesNeeded));
      } catch (error) {
        console.error('Error calculating page count:', error);
      }
    };

    // Try multiple times to ensure content is loaded
    const intervals = [100, 300, 500, 1000];
    const timeouts: NodeJS.Timeout[] = [];
    
    intervals.forEach(delay => {
      const timeout = setTimeout(checkContentHeight, delay);
      timeouts.push(timeout);
    });

    return () => {
      timeouts.forEach(clearTimeout);
      if (hiddenIframeRef.current) {
        hiddenIframeRef.current.removeEventListener('load', checkContentHeight);
      }
    };
  }, [document]);

  // Always render the toggle button with proper positioning for animation
  const toggleButton = (
    <div 
      className={`${styles.toggleButtonOpen} ${isOpenWide ? styles.openWide : ''} ${isEmbedded ? styles.embedded : ''}`}
    >
      <Tooltip content={isOpenWide ? "Close wide view" : "Open wide view"} position="left">
        <button 
          className={styles.toggleButton}
          onClick={onToggleWide || onToggle}
          aria-label={isOpenWide ? "Close wide view" : "Open wide view"}
        >
          {isOpenWide ? (
            // Right-pointing arrow when in wide mode (to close wide)
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="9,18 15,12 9,6"/>
            </svg>
          ) : (
            // Left-pointing arrow when in normal mode (to open wide)
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="15,18 9,12 15,6"/>
            </svg>
          )}
        </button>
      </Tooltip>
    </div>
  );

  return (
    <>
      {toggleButton}
      <div 
        className={`${styles.panel} ${isOpenWide ? styles.openWide : ''} ${isEmbedded ? styles.embedded : ''}`} 
        style={{ width: isOpenWide ? '80vw' : `${width}px` }}
      >
        {!isOpenWide && (
          <div 
            className={styles.resizeHandle}
            onMouseDown={handleMouseDown}
            aria-label="Resize panel"
          />
        )}
        
        <div className={styles.header}>
          <h3 className={styles.title}>Resume Draft</h3>
          {versionId && (
            <Tooltip content="View version history">
              <button
                className={styles.historyButton}
                onClick={() => setShowHistory(true)}
                aria-label="View version history"
              >
                <Clock size={18} />
                History
              </button>
            </Tooltip>
          )}
        </div>
        
        <div className={styles.content}>
          {document && hasResumeContent(document) ? (
            <>
              <div className={styles.paperPreviewContainer}>
                <div className={styles.pagesContainer}>
                  {Array.from({ length: pageCount }, (_, index) => (
                    <div
                      key={index}
                      ref={index === 0 ? paperWrapperRef : undefined}
                      className={styles.paperWrapper}
                      style={{
                        width: `${816 * scale}px`,
                        height: `${1056 * scale}px`
                      }}
                    >
                      <div 
                        className={styles.pageClipContainer}
                        style={{
                          transform: `scale(${scale})`,
                          transformOrigin: 'top left',
                          width: '816px',
                          height: '1056px'
                        }}
                      >
                        <iframe
                          className={styles.resumeIframe}
                          style={{
                            position: 'absolute',
                            top: `-${index * 1056}px`,
                            left: 0,
                            width: '816px',
                            height: `${Math.max(contentHeight, (pageCount * 1056))}px`
                          }}
                          srcDoc={processedDocument || DOMPurify.sanitize(document, {
                            ALLOWED_TAGS: [
                              'div', 'span', 'h1', 'h2', 'h3', 'p', 'ul', 'ol', 'li', 
                              'strong', 'em', 'br', 'a', 'style', 'head', 'title', 'meta', 'body', 'html', 'link'
                            ],
                            ALLOWED_ATTR: [
                              'class', 'id', 'style', 'href', 'target', 'rel',
                              'charset', 'name', 'content', 'lang', 'viewport', 'type'
                            ],
                            ALLOW_DATA_ATTR: false,
                            WHOLE_DOCUMENT: true
                          })}
                          title={`Resume Preview - Page ${index + 1}`}
                          sandbox="allow-same-origin"
                          frameBorder="0"
                          scrolling="no"
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              {/* Hidden iframe for measuring content height */}
              <iframe
                ref={hiddenIframeRef}
                style={{ 
                  position: 'absolute',
                  left: '-9999px',
                  width: '816px',
                  height: '1056px',
                  border: 'none'
                }}
                srcDoc={DOMPurify.sanitize(document, {
                  ALLOWED_TAGS: [
                    'div', 'span', 'h1', 'h2', 'h3', 'p', 'ul', 'ol', 'li', 
                    'strong', 'em', 'br', 'a', 'style', 'head', 'title', 'meta', 'body', 'html', 'link'
                  ],
                  ALLOWED_ATTR: [
                    'class', 'id', 'style', 'href', 'target', 'rel',
                    'charset', 'name', 'content', 'lang', 'viewport', 'type'
                  ],
                  ALLOW_DATA_ATTR: false,
                  WHOLE_DOCUMENT: true
                })}
                title="Hidden iframe for measurement"
                sandbox="allow-same-origin"
              />
            </>
          ) : (
            <div className={styles.emptyState}>
              <div className={styles.emptyIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                  <polyline points="14,2 14,8 20,8"/>
                  <line x1="16" y1="13" x2="8" y2="13"/>
                  <line x1="16" y1="17" x2="8" y2="17"/>
                  <line x1="10" y1="9" x2="8" y2="9"/>
                </svg>
              </div>
              <p className={styles.emptyText}>Your resume will appear here as we build it together.</p>
            </div>
          )}
        </div>
      </div>
      
      {showHistory && versionId && (
        <HistoryViewer
          versionId={versionId}
          onClose={() => setShowHistory(false)}
          onRestore={() => {
            setShowHistory(false);
            if (onHistoryRestore) {
              onHistoryRestore();
            }
          }}
        />
      )}
    </>
  );
};

export default ResumeDraft;
